import { SkeletonButton } from '@dev-spendesk/grapes';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useQueryError } from 'common/components/QueryError';
import { useNotifications } from 'modules/app/notifications';
import { unwrapQuery } from 'src/core/api/unwrapQuery';

import {
  PurchaseOrderAutocompleteSearch,
  type PurchaseOrderAutocompleteSearchProps,
} from './PurchaseOrderAutocompleteSearch';
import {
  usePurchaseOrderQuery,
  usePurchaseOrderQueryPromise,
} from '../../detail/hooks';
import { type PurchaseOrderDetails } from '../../detail/models';
import { useFetchPurchaseOrdersQuery } from '../../hooks';
import { type PurchaseOrder } from '../../models';
import { NoAvailablePurchaseOrderMessage } from '../NoAvailablePurchaseOrderMessage';

const NUMBER_OF_PURCHASE_ORDERS_IN_AUTOCOMPLETE = 50;

type Props = Omit<
  PurchaseOrderAutocompleteSearchProps,
  'purchaseOrders' | 'value' | 'onSearch'
> & {
  showNoAvailablePurchaseOrderMessage: boolean;
  value: PurchaseOrder['id'] | undefined;
  onPurchaseOrderDetailsFetched?: (
    purchaseOrderDetails: PurchaseOrderDetails | undefined,
  ) => void;
};
export const PurchaseOrderAutocomplete = ({
  value: rawValue,
  onSelect,
  onPurchaseOrderDetailsFetched,
  showNoAvailablePurchaseOrderMessage,
  ...restProps
}: Props) => {
  const value = isEmpty(rawValue) ? undefined : rawValue;
  const [search, setSearch] = useState<string | undefined>(undefined);
  const { selectedPurchaseOrderRef, isLoading } =
    useFindSelectedPurchaseOrderById({
      value,
      onSelect,
      onPurchaseOrderDetailsFetched,
    });

  const fetchPurchaseOrder = usePurchaseOrderQueryPromise();
  const purchaseOrdersQueryState = useFetchPurchaseOrdersQuery(
    {
      search,
      status: 'open',
    },
    NUMBER_OF_PURCHASE_ORDERS_IN_AUTOCOMPLETE,
  );
  const purchaseOrders = unwrapQuery(purchaseOrdersQueryState) ?? [];
  const purchaseOrdersIncludingSelection: PurchaseOrder[] = useMemo(
    () => [
      ...purchaseOrders,
      ...(selectedPurchaseOrderRef.current &&
      !purchaseOrders.some(
        (purchaseOrder) =>
          purchaseOrder.id === selectedPurchaseOrderRef.current?.id,
      )
        ? [selectedPurchaseOrderRef.current]
        : []),
    ],
    [purchaseOrders, selectedPurchaseOrderRef.current],
  );

  return isLoading ? (
    <SkeletonButton width={restProps.fit === 'parent' ? '100%' : undefined} />
  ) : (
    <>
      <PurchaseOrderAutocompleteSearch
        isLoading={purchaseOrdersQueryState.status === 'loading'}
        purchaseOrders={purchaseOrdersIncludingSelection}
        value={selectedPurchaseOrderRef.current}
        onSelect={async (purchaseOrder) => {
          onSelect(purchaseOrder);
          selectedPurchaseOrderRef.current = purchaseOrder;
          if (onPurchaseOrderDetailsFetched) {
            const purchaseOrderDetails = await (purchaseOrder
              ? fetchPurchaseOrder(purchaseOrder.id)
              : undefined);

            // condition below prevents potential race condition
            if (
              purchaseOrderDetails?.id === selectedPurchaseOrderRef.current?.id
            ) {
              onPurchaseOrderDetailsFetched?.(purchaseOrderDetails);
            }
          }
        }}
        onSearch={setSearch}
        {...restProps}
      />
      {search === '' &&
        purchaseOrders.length === 0 &&
        showNoAvailablePurchaseOrderMessage && (
          <NoAvailablePurchaseOrderMessage className="mt-8" />
        )}
    </>
  );
};

const useFindSelectedPurchaseOrderById = ({
  value,
  onSelect,
  onPurchaseOrderDetailsFetched,
}: Pick<Props, 'value' | 'onSelect' | 'onPurchaseOrderDetailsFetched'>) => {
  const selectedPurchaseOrderReference = useRef<PurchaseOrder | undefined>(
    null,
  );

  const { dangerNotif } = useNotifications();
  const getQueryError = useQueryError({
    requestError: () => 'purchaseOrders.query.autocompleteError',
    serverError: 'purchaseOrders.query.autocompleteError',
  });

  useEffect(() => {
    if (
      value === undefined &&
      selectedPurchaseOrderReference.current !== undefined
    ) {
      selectedPurchaseOrderReference.current = undefined;
    }
  }, [value]);

  const shouldFetchPurchaseOrder =
    Boolean(value) && value !== selectedPurchaseOrderReference.current?.id;
  const purchaseOrderQueryState = usePurchaseOrderQuery(value, {
    enabled: shouldFetchPurchaseOrder,
    onSuccess: (purchaseOrder) => {
      if (purchaseOrder.id !== selectedPurchaseOrderReference.current?.id) {
        selectedPurchaseOrderReference.current = purchaseOrder;
        onSelect(purchaseOrder);
        onPurchaseOrderDetailsFetched?.(purchaseOrder);
      }
    },
    onError: (error) => {
      selectedPurchaseOrderReference.current = undefined;
      onSelect(undefined);
      dangerNotif(getQueryError(error));
    },
  });

  return {
    selectedPurchaseOrderRef: selectedPurchaseOrderReference,
    isLoading:
      purchaseOrderQueryState.status === 'loading' && shouldFetchPurchaseOrder,
  };
};
